/* eslint-disable react/no-array-index-key */
import {
  HTMLAttributes,
  useMemo,
  memo,
  useState,
  useCallback,
  useEffect,
  useRef,
  Fragment,
} from 'react';
import styled from '@emotion/styled';
import { useTranslate } from '@tolgee/react';
import { Button as AntButton, Divider, Popover } from 'antd';
import { LeftOutlined, RightOutlined, LoadingOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs from 'lib/dayjs';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import detailSvg from 'views/Aide/assets/more.svg';
import useChatHistory from 'views/Refund/components/useChatHIstory';
import SiderImageSlide from './SiderImageSlide';
import useSlide from './useSlide';
import useTradeTranslate, { getTranslation } from './useTradeTranslate';
import { useTradeListContext } from './TradeContext';
import { useReturnReasonLabel } from './hooks/useReturnReasonLabel';

const Container = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  > div {
    flex: 1 0 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  > span {
    flex: 0 0 auto;
    margin-left: 10px;
  }
`;

export const PopoverContent = styled.div`
  width: 280px;
  max-height: 360px;
  margin: -12px -16px;
  padding: 15px;
  overflow: auto;
  > * {
    margin-bottom: 10px;
  }
`;

export const Between = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const Title = styled.h5`
  margin-bottom: 0;
  font-size: 14px;
  color: #515359;
`;

export const Button = styled(AntButton)`
  padding: 0;
  font-size: 12px;
  cursor: pointer;
`;

const Time = styled.span`
  font-size: 12px;
  color: #84868c;
`;

export const Operation = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 40px;
`;

export const Action = styled.span<{ disabled?: boolean }>`
  width: 16px;
  height: 16px;
  line-height: 16px;
  font-size: 10px;
  text-align: center;
  color: ${({ disabled = false }) => (disabled ? '#D9DDE4' : '#A1A3A8')};
  background: #f0f2f5;
  cursor: ${({ disabled = false }) => (disabled ? 'not-allowed' : 'pointer')};
`;

export const Text = styled.p`
  font-size: 12px;
  color: #515359;
  word-break: break-all;
`;

export const Trigger = styled.span<{ visible?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 2px;
  cursor: pointer;
  /* background: ${({ visible }) => (visible === 'true' ? '#E1E4EB' : 'transparent')}; */
  /* &:hover,
  &:focus {
    background: #e1e4eb;
  } */
`;

const ReasonTip = styled.div`
  color: #515359;
`;

const DetailImg = styled.img`
  width: 16px;
  height: 16px;
`;

const isTranslationValid = (
  refundReasons: Conversation.Trade.RefundReason[],
  lang?: General.SystemLanguage,
) => {
  return refundReasons.every(({ textReasonTrans }) => {
    const translation = getTranslation(textReasonTrans, lang);

    return typeof translation === 'string';
  });
};

export interface TradeRefundProps extends HTMLAttributes<HTMLDivElement> {
  platform: General.PlatformUpperCase;
  refundReasons: Conversation.Trade.RefundReason[];
  product: Conversation.Trade.Product;
  productIndex: number;
  region: General.UpperCaseRegion;
  storeId: string;
  tradeId: string;
}

export const imageCount = 3;

function TradeRefund(props: TradeRefundProps) {
  const {
    platform,
    refundReasons,
    product,
    productIndex,
    region,
    storeId,
    tradeId,
    ...otherProps
  } = props;

  const { conversation } = useTradeListContext();

  const { t } = useTranslate();

  const systemLang = useSystemLanguageValue();

  const [visible, setVisible] = useState(false);

  const [displayTranslation, setDisplayTranslation] = useState(() =>
    isTranslationValid(refundReasons),
  );

  const { pending: translating, error: translationError, translateRefund } = useTradeTranslate();

  const firstReason = refundReasons?.[0];
  const reassessedRequestReason = refundReasons.find(
    (_) => _.reassessedRequestReason,
  )?.reassessedRequestReason;

  const { reasonLabel: tradeReason, reassessedRequestReasonLabel: reassessedRequestReasonText } =
    useReturnReasonLabel({
      platform,
      reason: firstReason?.reason,
      reasonName: firstReason?.reasonName,
      reassessedRequestReason,
    });

  const onTranslate = useCallback(() => {
    if (!conversation) {
      return;
    }

    if (displayTranslation) {
      setDisplayTranslation(false);

      return;
    }

    if (isTranslationValid(refundReasons, systemLang)) {
      setDisplayTranslation(true);

      return;
    }

    translateRefund(product, productIndex)
      .then(() => {
        setDisplayTranslation(true);
      })
      .catch(() => {});
  }, [
    conversation,
    displayTranslation,
    product,
    refundReasons,
    translateRefund,
    productIndex,
    systemLang,
  ]);

  const button = useMemo(() => {
    if (refundReasons.length === 0) {
      return null;
    }

    const [{ textReason }] = refundReasons;

    if (!textReason) {
      return null;
    }

    if (translating) {
      return (
        <Button type="link" loading icon={<LoadingOutlined />}>
          {t('trade.translatingMsg')}
        </Button>
      );
    }

    if (translationError) {
      return (
        <Button type="link" icon={<ReloadOutlined />} onClick={onTranslate}>
          {t('trade.翻译出错，点击重翻')}
        </Button>
      );
    }

    return (
      <Button type="link" onClick={onTranslate}>
        {t(`trade.${displayTranslation ? '查看原文' : '查看翻译'}`)}
      </Button>
    );
  }, [t, translating, translationError, displayTranslation, onTranslate, refundReasons]);

  const refsCurrent = {
    refundReasons,
    onTranslate,
  };
  const refs = useRef(refsCurrent);
  refs.current = refsCurrent;

  useEffect(() => {
    const {
      current: { refundReasons, onTranslate },
    } = refs;

    if (isTranslationValid(refundReasons, systemLang)) {
      return;
    }

    setDisplayTranslation(false);

    onTranslate();
  }, [systemLang]);

  if (refundReasons.length === 0) {
    return null;
  }

  const popoverContent = (
    <PopoverContent onClick={(e) => e.stopPropagation()}>
      <Between>
        <Title>{t('trade.退货/退款原因')}</Title>
        {button}
      </Between>
      <ReasonTip>{tradeReason}</ReasonTip>
      {refundReasons.map((reason, index) => {
        return (
          <Fragment key={reason.reason}>
            <Reason
              reason={reason}
              displayTranslation={displayTranslation}
              platform={platform}
              region={region}
              storeId={storeId}
              tradeId={tradeId}
            />
            {index < refundReasons.length - 1 && <Divider />}
          </Fragment>
        );
      })}
    </PopoverContent>
  );

  const reasonContent = (
    <Container {...otherProps}>
      <div title={tradeReason}>{`${t('trade.退货/退款原因')}: ${tradeReason}`}</div>
      <Popover
        visible={visible}
        onVisibleChange={setVisible}
        trigger="click"
        arrowPointAtCenter
        placement="bottomRight"
        content={popoverContent}
      >
        <Trigger visible={String(visible)} onClick={(e) => e.stopPropagation()}>
          <DetailImg src={detailSvg} alt="detail" />
        </Trigger>
      </Popover>
    </Container>
  );

  if (!reassessedRequestReasonText) {
    return reasonContent;
  }

  return (
    <>
      {reasonContent}
      <Container {...otherProps}>
        <div title={reassessedRequestReasonText}>
          {`${t('复核后退货退款原因')}: ${reassessedRequestReasonText}`}
        </div>
      </Container>
    </>
  );
}

interface ReasonProps {
  reason: Conversation.Trade.RefundReason;
  displayTranslation: boolean;
  platform: General.PlatformUpperCase;
  region: General.UpperCaseRegion;
  storeId: string;
  tradeId: string;
}

function Reason(props: ReasonProps) {
  const { platform, reason, displayTranslation, region, storeId, tradeId } = props;
  const {
    image = [],
    returnVideo = [],
    textReason,
    textReasonTrans,
    returnCreateTime,
    returnOrderItemId,
    returnSn,
  } = reason;
  const translation = getTranslation(textReasonTrans);
  const [openChatHistory, chatHistoryModal] = useChatHistory();
  const { t } = useTranslate();

  const video = returnVideo.map(({ videoUrl }) => videoUrl);

  const imageLength = image.length + video.length;

  const {
    index: imageIndex,
    preDisabled,
    nextDisabled,
    pre,
    next,
  } = useSlide({
    count: imageCount,
    length: imageLength,
    initialIndex: 0,
  });

  return (
    <>
      <Between>
        <Time>{dayjs(Number(returnCreateTime) * 1000).format('YYYY-MM-DD HH:mm:ss')}</Time>
        {imageCount <= imageLength && (
          <Operation>
            <Action disabled={preDisabled} onClick={pre}>
              <LeftOutlined />
            </Action>
            <Action disabled={nextDisabled} onClick={next}>
              <RightOutlined />
            </Action>
          </Operation>
        )}
      </Between>
      {imageLength > 0 && (
        <SiderImageSlide
          videos={video}
          images={image}
          count={imageCount}
          index={imageIndex}
          width={640}
          offsetPercent={2}
          extra={{ platform, region, storeId, tradeId }}
        />
      )}
      <Text>{displayTranslation ? translation : textReason}</Text>
      {platform === 'LAZADA' && (
        <Button type="link" onClick={() => openChatHistory(returnSn, returnOrderItemId)}>
          {t('ViewRefund.通讯历史')}
        </Button>
      )}
      {chatHistoryModal}
    </>
  );
}

export default memo(TradeRefund);
